/**
 * INVOICE_PAYMENT_ACTION (AF-4126): grade com uma linha por natureza do título. A primeira coluna é
 * onde a escolha aparece (`controls`); as outras três são as fontes de pagamento, cada célula com o
 * resumo dos padrões do par e, ao clicar, o modal que os edita.
 */
import { useState } from 'react';
import SelectTextField from '../../../molecules/SelectTextField';
import { parseJson } from '../services/businessPropertyRules';
import InvoicePaymentActionPairModal from './InvoicePaymentActionPairModal';
import {
    ActionPair, CONTROLS_OPTIONS, InvoicePaymentAction, pairOf, PARTIAL, parentLabel, referenceKey, REFERENCES,
    SOURCES, TOTAL, withControls, withPair,
} from './invoicePaymentActionModel';

interface Props {
    value: string;
    onChange: (value: string) => void;
}

interface Selection {
    reference: string;
    source: string;
}

const CELL = 'text-left w-full h-full p-3 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-blue-400 dark:hover:border-blue-400 text-xs text-gray-700 dark:text-gray-200';
const TITLE = 'font-semibold text-gray-600 dark:text-gray-300';

const PairSummary = ({ pair }: { pair: ActionPair | undefined }) => {
    if (!pair) return <span>—</span>;
    return (
        <>
            <p className={TITLE}>Padrões quando Liquidação:</p>
            <p className="ml-2">Fatura Anterior: {parentLabel(pair[TOTAL].parent)}</p>
            <p className={`${TITLE} mt-2`}>Padrões quando Pagamento Parcial:</p>
            <p className="ml-2">Fatura Anterior: {parentLabel(pair[PARTIAL].parent)}</p>
            <p className="ml-2">Fatura Residual: {pair[PARTIAL].residual}</p>
        </>
    );
};

const InvoicePaymentActionEditor = ({ value, onChange }: Props) => {
    const [selection, setSelection] = useState<Selection | null>(null);
    const action = parseJson<InvoicePaymentAction>(value, {});
    const selectedPair = selection ? pairOf(action, selection.reference, selection.source) : null;

    return (
        <div className="overflow-x-auto">
            <div className="grid grid-cols-[minmax(180px,1fr)_repeat(3,minmax(200px,1fr))] gap-3 min-w-[820px]">
                <span />
                {SOURCES.map(source => (
                    <span key={source} className="text-sm font-semibold text-gray-700 dark:text-gray-200">{source}</span>
                ))}
                {REFERENCES.map(reference => (
                    <div key={reference} className="contents">
                        <div className="flex flex-col gap-2">
                            <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">{reference}</span>
                            <SelectTextField
                                id={`controls-${reference}`}
                                label="Onde mostrar:"
                                items={CONTROLS_OPTIONS}
                                hideEmptyOption
                                value={action[referenceKey(reference)]?.controls ?? ''}
                                onChange={e => onChange(JSON.stringify(withControls(action, reference, e.target.value)))}
                            />
                        </div>
                        {SOURCES.map(source => (
                            <button
                                key={source}
                                type="button"
                                aria-label={`${reference} × ${source}`}
                                className={CELL}
                                onClick={() => setSelection({ reference, source })}
                            >
                                <PairSummary pair={pairOf(action, reference, source)} />
                            </button>
                        ))}
                    </div>
                ))}
            </div>
            {selection && (
                <InvoicePaymentActionPairModal
                    key={`${selection.reference}-${selection.source}`}
                    reference={selection.reference}
                    source={selection.source}
                    pair={selectedPair}
                    onClose={() => setSelection(null)}
                    onApply={pair => {
                        onChange(JSON.stringify(withPair(action, selection.reference, selection.source, pair)));
                        setSelection(null);
                    }}
                />
            )}
        </div>
    );
};

export default InvoicePaymentActionEditor;
